import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

import { reportError } from "../../utils/Instrumentation";
import { createApiClient, apiClient } from "../../api/apiClient";

// Define types for the Contact Us slice
export interface Category {
  id: number;
  name: string;
  ID?: number; // Optional for compatibility with existing data structures
  Name?: string; // Optional for compatibility with existing data structures
}

export interface IssueType {
  id: number;
  name: string;
  ID?: number; // Optional for compatibility with existing data structures
  Name?: string; // Optional for compatibility with existing data structures
}

// For regular JSON payload (legacy)
export interface FeedbackPayload {
  CategoryID: number;
  IssueTypeID: number;
  Urgency: string;
  Feedback: string;
  AttachmentUrl?: string;
  Created_By: string;
}

// For FormData payload with file attachment
export interface FeedbackFormDataPayload {
  formData: FormData;
}

interface ContactUsState {
  categories: Category[];
  issueTypes: IssueType[];
  isLoading: boolean;
  isSubmitting: boolean;
  submitSuccess: boolean;
  submitError: boolean;
  errorMessage: string | null;
  centralizedErrors: {
    errors: any[];
    activeError: any | null;
    isErrorModalVisible: boolean;
  };
}

// Initial state
const initialState: ContactUsState = {
  categories: [],
  issueTypes: [],
  isLoading: false,
  isSubmitting: false,
  submitSuccess: false,
  submitError: false,
  errorMessage: null,
  // centralized errors for Contact Us
  centralizedErrors: {
    errors: [],
    activeError: null,
    isErrorModalVisible: false,
  },
};

// Helper to generate simple unique id for errors
const generateErrorId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

// Helper to combine friendly message and API original message with proper punctuation
const buildConcatenatedMessage = (originalRaw: any, friendlyMessage: string) => {
  const original = originalRaw ? String(originalRaw).trim() : "";
  const originalForDisplay = original ? (/[.!?]$/.test(original) ? original : `${original}.`) : "";
  const retrySuffix = ' Please try again.';

  if (original) {
    if (friendlyMessage.endsWith(retrySuffix)) {
      const base = friendlyMessage.slice(0, -retrySuffix.length);
      return `${base} ${originalForDisplay}${retrySuffix}`;
    }
    return `${friendlyMessage} ${originalForDisplay}`;
  }
  return friendlyMessage;
};

// Async thunks for API calls
export const fetchCategories = createAsyncThunk(
  "contactUs/fetchCategories",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const api = createApiClient();
      const data = await apiClient(api, "/contact-us/category", "GET");
      return data;
    } catch (error: any) {
      reportError({
        severity: 'ERROR',
        message: error instanceof Error ? error.message : String(error),
        stackTrace: error instanceof Error ? error.stack : undefined,
      });

      const originalMessage = error?.response?.data?.message || error?.response?.data?.error_msg || error?.message;
      const friendly = 'Failed to fetch categories. Please try again.';
      const concatenated = buildConcatenatedMessage(originalMessage, friendly);

      // Build centralized error object and dispatch modal
      const centralizedError = {
        id: generateErrorId(),
        message: concatenated,
        details: originalMessage,
        apiName: "/contact-us/category",
        timestamp: Date.now(),
        context: { originalMessage },
      };

      try { dispatch((contactUsSlice.actions as any).showErrorModal(centralizedError)); } catch (e) { /* swallow */ }

      return rejectWithValue(concatenated);
    }
  }
);

export const fetchIssueTypes = createAsyncThunk(
  "contactUs/fetchIssueTypes",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const api = createApiClient();
      const data = await apiClient(api, "/contact-us/issue_type", "GET");
      return data;
    } catch (error: any) {
      reportError({
        severity: 'ERROR',
        message: error instanceof Error ? error.message : String(error),
        stackTrace: error instanceof Error ? error.stack : undefined,
      });

      const originalMessage = error?.response?.data?.message || error?.response?.data?.error_msg || error?.message;
      const friendly = 'Failed to fetch issue types. Please try again.';
      const concatenated = buildConcatenatedMessage(originalMessage, friendly);

      const centralizedError = {
        id: generateErrorId(),
        message: concatenated,
        details: originalMessage,
        apiName: "/contact-us/issue_type",
        timestamp: Date.now(),
        context: { originalMessage },
      };

      try { dispatch((contactUsSlice.actions as any).showErrorModal(centralizedError)); } catch (e) { /* swallow */ }

      return rejectWithValue(concatenated);
    }
  }
);

export const submitFeedback = createAsyncThunk(
  "contactUs/submitFeedback",
  async (
    payload: FeedbackPayload | FeedbackFormDataPayload,
    { rejectWithValue, getState, dispatch }
  ) => {
    try {
      const api = createApiClient();
      // Get state to access auth token directly from Redux store
      const state = getState() as any;
      const token = state.auth?.user?.idToken || state.auth?.token;
      console.log("Auth token available:", !!token);

      const buildConcatenatedMessageInner = (originalRaw: any, friendlyMessage: string) => {
        const original = originalRaw ? String(originalRaw).trim() : "";
        const originalForDisplay = original ? (/[.!?]$/.test(original) ? original : `${original}.`) : "";
        const retrySuffix = ' Please try again.';

        if (original) {
          if (friendlyMessage.endsWith(retrySuffix)) {
            const base = friendlyMessage.slice(0, -retrySuffix.length);
            return `${base} ${originalForDisplay}${retrySuffix}`;
          }
          return `${friendlyMessage} ${originalForDisplay}`;
        }
        return friendlyMessage;
      };

      const friendlySubmitMessage = 'Failed to submit feedback. Please try again.';

      if ("formData" in payload) {
        // Use axios directly for FormData to leverage the interceptors
        try {
          console.log("Sending FormData using axios");

          // This approach will automatically include the Authorization header from the interceptors
          const response = await api.post("/contact-us", payload.formData, {
            headers: {
              // Let the browser set the content-type automatically for FormData
              "Content-Type": "multipart/form-data",
            },
          });

          console.log("FormData submission successful");
          return response.data;
        } catch (axiosError: any) {
          console.error(
            "FormData submission error:",
            axiosError?.response?.data || axiosError
          );

          const originalMessage =
            axiosError?.response?.data?.message ||
            axiosError?.response?.data?.error_msg ||
            axiosError?.message;

          const concatenated = buildConcatenatedMessageInner(originalMessage, friendlySubmitMessage);

          const centralizedError = {
            id: generateErrorId(),
            message: concatenated,
            details: originalMessage,
            apiName: "/contact-us",
            timestamp: Date.now(),
            context: { originalMessage },
          };

          try { dispatch((contactUsSlice.actions as any).showErrorModal(centralizedError)); } catch (e) { /* swallow */ }

          // Return a rejected value so the thunk resolves to rejected once
          return rejectWithValue(concatenated);
        }
      } else {
        // Regular JSON payload (legacy)
        const data = await apiClient(api, "/contact-us", "POST", payload);
        return data;
      }
    } catch (error: any) {
      console.error("Submit Feedback Error:", error);

      // Extract original API message when available and concatenate with friendly message
      const originalMessage = error?.response?.data?.message || error?.response?.data?.error_msg || error?.message;
      const friendlySubmitMessage = 'Failed to submit feedback. Please try again.';
      const original = originalMessage ? String(originalMessage).trim() : '';
      const originalForDisplay = original ? (/[.!?]$/.test(original) ? original : `${original}.`) : '';
      const retrySuffix = ' Please try again.';
      let concatenatedMessage: string;
      if (original) {
        if (friendlySubmitMessage.endsWith(retrySuffix)) {
          const base = friendlySubmitMessage.slice(0, -retrySuffix.length);
          concatenatedMessage = `${base} ${originalForDisplay}${retrySuffix}`;
        } else {
          concatenatedMessage = `${friendlySubmitMessage} ${originalForDisplay}`;
        }
      } else {
        concatenatedMessage = friendlySubmitMessage;
      }

      const centralizedError = {
        id: generateErrorId(),
        message: concatenatedMessage,
        details: originalMessage,
        apiName: "/contact-us",
        timestamp: Date.now(),
        context: { originalMessage },
      };

      try { dispatch((contactUsSlice.actions as any).showErrorModal(centralizedError)); } catch (e) { /* swallow */ }

      reportError({
        severity: 'ERROR',
        message: error instanceof Error ? error.message : String(error),
        stackTrace: error instanceof Error ? error.stack : undefined,
      });
      return rejectWithValue(concatenatedMessage || "Failed to submit feedback");
    }
  }
);

// Create the Contact Us slice
const contactUsSlice = createSlice({
  name: "contactUs",
  initialState,
  reducers: {
    resetFormStatus(state) {
      state.submitSuccess = false;
      state.submitError = false;
      state.errorMessage = null;
    },

    // centralized error handling
    addCentralizedError: (state, action: PayloadAction<any>) => {
      state.centralizedErrors.errors.push(action.payload);
      if (state.centralizedErrors.errors.length > 10) {
        state.centralizedErrors.errors = state.centralizedErrors.errors.slice(-10);
      }
    },

    showErrorModal(state, action: PayloadAction<any>) {
      state.centralizedErrors.activeError = action.payload;
      state.centralizedErrors.isErrorModalVisible = true;
      state.centralizedErrors.errors.push(action.payload);
      if (state.centralizedErrors.errors.length > 10) {
        state.centralizedErrors.errors = state.centralizedErrors.errors.slice(-10);
      }
    },

    hideErrorModal(state) {
      state.centralizedErrors.isErrorModalVisible = false;
      state.centralizedErrors.activeError = null;
    },

    removeErrorById(state, action: PayloadAction<string>) {
      state.centralizedErrors.errors = state.centralizedErrors.errors.filter((e: any) => e.id !== action.payload);
      if (state.centralizedErrors.activeError?.id === action.payload) {
        state.centralizedErrors.activeError = null;
        state.centralizedErrors.isErrorModalVisible = false;
      }
    },

    clearAllErrors(state) {
      state.centralizedErrors.errors = [];
      state.centralizedErrors.activeError = null;
      state.centralizedErrors.isErrorModalVisible = false;
    },
  },
  extraReducers: (builder) => {
    // Fetch categories
    builder.addCase(fetchCategories.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(
      fetchCategories.fulfilled,
      (state, action: PayloadAction<Category[]>) => {
        state.categories = action.payload;
        state.isLoading = false;
      }
    );
    builder.addCase(fetchCategories.rejected, (state, action) => {
      state.isLoading = false;
      state.errorMessage = action.payload as string;
    });

    // Fetch issue types
    builder.addCase(fetchIssueTypes.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(
      fetchIssueTypes.fulfilled,
      (state, action: PayloadAction<IssueType[]>) => {
        state.issueTypes = action.payload;
        state.isLoading = false;
      }
    );
    builder.addCase(fetchIssueTypes.rejected, (state, action) => {
      state.isLoading = false;
      state.errorMessage = action.payload as string;
    });

    // Submit feedback
    builder.addCase(submitFeedback.pending, (state) => {
      state.isSubmitting = true;
      state.submitSuccess = false;
      state.submitError = false;
    });
    builder.addCase(submitFeedback.fulfilled, (state) => {
      state.isSubmitting = false;
      state.submitSuccess = true;
      state.submitError = false;
    });
    builder.addCase(submitFeedback.rejected, (state, action) => {
      state.isSubmitting = false;
      state.submitSuccess = false;
      state.submitError = true;
      state.errorMessage = action.payload as string;
    });
  },
});

export const {
  resetFormStatus,
  // centralized error actions
  addCentralizedError,
  showErrorModal,
  hideErrorModal,
  removeErrorById,
  clearAllErrors,
} = contactUsSlice.actions;
export default contactUsSlice.reducer;
